/**
 * The push channel. The client half of `shared/ws.ts`.
 *
 * No dependency: `partysocket` is 12 KB to reconnect a socket that has one message type, and the
 * three things that actually need care here are all things a library would hide rather than
 * solve. They are:
 *
 * **The channel is PUSH ONLY.** The only frame this client ever sends is the two-byte heartbeat,
 * which the runtime answers from the edge without waking the Durable Object, without accruing
 * duration and without counting toward the 20:1 inbound billing ratio. Every mutation in the game
 * goes over HTTP, which is what gives all of them the same idempotency key, action token, rate
 * limit and audit path.
 *
 * **A SEQUENCE GAP IS NOT PATCHED, IT IS RECONNECTED.** Every frame carries a monotonic `seq`. If
 * one arrives that is not exactly one past the last, this closes the socket and reconnects with
 * `?since=last`, which routes it into either a replay or a full snapshot. Silently tolerating a
 * gap is how a family roster drifts out of sync for an hour and nobody can say when it started.
 *
 * **EVERY DEPLOY KILLS EVERY SOCKET.** Not an edge case, a scheduled event: the Workers runtime
 * updates a few times a week regardless of our own deploys, and WebSockets get zero grace, no
 * drain and no shutdown hook. So reconnection is the normal path rather than the error path, and
 * the backoff is jittered because otherwise every member of every family comes back at once.
 */

import type { Frame, ServerMessageType, ServerPayload } from '@ashport/shared/ws'
import { HEARTBEAT_REQUEST, HEARTBEAT_RESPONSE } from '@ashport/shared/ws'
import { API_ORIGIN } from '../build'
import { getBearer } from './api'

/** Jittered, because the reconnect after a deploy is the whole population at the same instant. */
const MIN_BACKOFF_MS = 1000
const MAX_BACKOFF_MS = 10_000
const BACKOFF_GROWTH = 1.3
/** The runtime answers this itself. A silent line for two of them means the socket is dead. */
const HEARTBEAT_MS = 25_000
const HEARTBEAT_GRACE_MS = 60_000

export type FrameHandler = <T extends ServerMessageType>(t: T, p: ServerPayload[T], seq: number) => void

export interface RoomOptions {
  /** `/ws/family/{id}`. Same origin, so the session cookie rides the handshake. */
  path: string
  onFrame: FrameHandler
  /** Called when the room says to replace local state outright rather than patch it. */
  onResync: (snapshot: unknown) => void
  onStatus?: (status: 'connecting' | 'open' | 'closed') => void
}

export interface Room {
  close: () => void
  status: () => 'connecting' | 'open' | 'closed'
}

/**
 * Open a room and keep it open.
 *
 * Returns a handle rather than an event emitter: there is exactly one consumer of a room in this
 * client, and an emitter would be indirection with no second subscriber to justify it.
 */
export function openRoom(o: RoomOptions): Room {
  let ws: WebSocket | null = null
  let lastSeq = 0
  let backoff = MIN_BACKOFF_MS
  let closedByUs = false
  let status: 'connecting' | 'open' | 'closed' = 'connecting'
  let heartbeat: ReturnType<typeof setInterval> | null = null
  let lastPong = 0
  let retry: ReturnType<typeof setTimeout> | null = null

  const setStatus = (s: typeof status) => {
    status = s
    o.onStatus?.(s)
  }

  const stopHeartbeat = () => {
    if (heartbeat !== null) clearInterval(heartbeat)
    heartbeat = null
  }

  const url = (): string => {
    // Derived from `location`, never hardcoded: dev serves the Worker in-process on the same
    // origin through the Cloudflare Vite plugin, so there is no proxy and no second port.
    //
    // PHASE 26. The Jest build is served from their CDN, so the room lives at `API_ORIGIN`
    // instead, and the session rides `&token=` because an upgrade cannot carry a header. The
    // server reads that parameter only on an upgrade and only when no cookie came (1ag.2).
    const at = API_ORIGIN !== '' ? new URL(API_ORIGIN) : window.location
    const proto = at.protocol === 'https:' ? 'wss:' : 'ws:'
    const bearer = getBearer()
    const token = bearer === null ? '' : `&token=${encodeURIComponent(bearer)}`
    return `${proto}//${at.host}${o.path}?since=${lastSeq}${token}`
  }

  const schedule = () => {
    if (closedByUs) return
    if (retry !== null) clearTimeout(retry)
    // Jitter on the FIRST delay as well as the growth, so a deploy does not produce a thundering
    // herd one second later.
    const wait = Math.min(MAX_BACKOFF_MS, backoff + Math.random() * MIN_BACKOFF_MS * 4)
    backoff = Math.min(MAX_BACKOFF_MS, backoff * BACKOFF_GROWTH)
    retry = setTimeout(connect, wait)
  }

  const connect = () => {
    if (closedByUs) return
    setStatus('connecting')
    let socket: WebSocket
    try {
      socket = new WebSocket(url())
    } catch {
      schedule()
      return
    }
    ws = socket

    socket.addEventListener('open', () => {
      backoff = MIN_BACKOFF_MS
      lastPong = Date.now()
      setStatus('open')
      stopHeartbeat()
      heartbeat = setInterval(() => {
        // A line that has answered nothing for two heartbeats is gone whether or not the browser
        // has noticed. `close()` here routes into the ordinary reconnect path.
        if (Date.now() - lastPong > HEARTBEAT_GRACE_MS) {
          try {
            socket.close()
          } catch {
            /* already gone */
          }
          return
        }
        if (socket.readyState === WebSocket.OPEN) socket.send(HEARTBEAT_REQUEST)
      }, HEARTBEAT_MS)
    })

    socket.addEventListener('message', (e) => {
      const raw = typeof e.data === 'string' ? e.data : ''
      // The auto-response. It never reaches the Durable Object and it is not a frame.
      if (raw === HEARTBEAT_RESPONSE) {
        lastPong = Date.now()
        return
      }
      let frame: Frame
      try {
        frame = JSON.parse(raw) as Frame
      } catch {
        return
      }
      lastPong = Date.now()

      if (frame.t === 'hello') {
        const p = frame.p as ServerPayload['hello']
        lastSeq = p.seq
        // `resumed: false` means the ring could not cover the gap, so what follows is a full
        // snapshot and local room state has to be replaced rather than patched.
        if (!p.resumed) lastSeq = p.seq
        return
      }
      if (frame.t === 'resync') {
        o.onResync((frame.p as ServerPayload['resync']).snapshot)
        return
      }

      // THE GAP CHECK. Anything other than exactly one past the last frame means this client has
      // missed something, and the only safe response is to go back and ask for it.
      if (lastSeq > 0 && frame.seq !== lastSeq + 1) {
        lastSeq = Math.min(lastSeq, frame.seq - 1)
        try {
          socket.close()
        } catch {
          /* already gone */
        }
        return
      }
      lastSeq = frame.seq
      o.onFrame(frame.t, frame.p as never, frame.seq)
    })

    const gone = () => {
      stopHeartbeat()
      if (closedByUs) {
        setStatus('closed')
        return
      }
      setStatus('closed')
      schedule()
    }
    socket.addEventListener('close', gone)
    socket.addEventListener('error', gone)
  }

  connect()

  return {
    close: () => {
      closedByUs = true
      stopHeartbeat()
      if (retry !== null) clearTimeout(retry)
      try {
        ws?.close()
      } catch {
        /* already gone */
      }
      setStatus('closed')
    },
    status: () => status,
  }
}
